import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import i18n from '../../i18n';
import DialogComponent from '../../component/dialog/dialog.component';
import CommonInput from '../../component/form/common-input.component';
import { FormFieldCell } from '../../component/form/form-field-cell.component';
import GlobalNotification from '../../component/common/snackbar';
import RichTextEditor from '../../component/rich-text-editor/rich-text-editor.component';
import { RBP_MENU_SETTINGS } from '../../constants/rbp-menu-settings.constant';

/** 条款弹窗：编辑保存 / 仅预览 */
export type ParkTermsDialogMode = 'edit' | 'preview';

/** 旧版 settings 条款详情（150/151/152） */
export type ParkTermsDetailLegacy = {
    /** 设置项 id（150/151/152） */
    id: number;
    /** 条款标题 */
    title: string;
    /** HTML 正文 */
    content: string;
};

/** 新版协议版本条款详情 */
export type ParkTermsDetailVersioned = {
    /** 版本记录 id */
    id: number;
    /** 列表展示标题 */
    title: string;
    /** 嵌套正文：标题 + HTML */
    content: {
        /** 可编辑标题（仅 TERMS_OF_USE） */
        title: string;
        /** 富文本 HTML */
        content: string;
    };
    /** 协议名 */
    agreement_name: string;
    /** 协议类型 */
    agreement_type: string;
    /** 语言 */
    language: string;
    /** 是否已有签署记录 */
    has_signed_record: boolean;
    /** 是否可修改 */
    can_modify: boolean;
};

/** 弹窗共用详情（新旧联合） */
export type ParkTermsDetail = ParkTermsDetailLegacy | ParkTermsDetailVersioned;

/**
 * 是否为新版协议版本详情
 * @param detail - 条款详情
 */
export function isParkTermsDetailVersioned(detail: ParkTermsDetail): detail is ParkTermsDetailVersioned {
    return (
        typeof detail === 'object' &&
        detail !== null &&
        'agreement_type' in detail &&
        typeof (detail as ParkTermsDetailVersioned).content === 'object' &&
        (detail as ParkTermsDetailVersioned).content !== null
    );
}

interface Props {
    open: boolean;
    detail: ParkTermsDetail;
    /** 编辑或可读的预览（仅关闭，不保存） */
    mode?: ParkTermsDialogMode;
    onClose: () => void;
    onSave: (value: ParkTermsDetail) => void;
}

/**
 * 条款编辑弹窗：标题 + 富文本正文；新版按 has_signed_record 切换「保存」/「保存并发布」。
 */
const ParkModeTermsDetailDialog: React.FC<Props> = ({ open, detail, mode = 'edit', onClose, onSave }) => {
    /** 弹窗内编辑态 */
    const [data, setData] = useState<ParkTermsDetail>(detail);

    useEffect(() => {
        setData(detail);
    }, [detail, open]);

    /** 是否新版协议详情 */
    const isVersioned = isParkTermsDetailVersioned(data);
    /** 预览模式 */
    const isPreview = mode === 'preview';

    /**
     * 是否禁用标题编辑
     * 新版：仅 TERMS_OF_USE 可改；旧版：仅 setting 150 可改
     */
    const isTitleDisabled = isPreview
        ? true
        : isVersioned
          ? data.agreement_type !== 'TERMS_OF_USE'
          : data.id !== RBP_MENU_SETTINGS.PARK_MODE_TERMS_CONDITIONS;

    /** 当前展示用标题字符串 */
    const titleValue = isVersioned ? data.content.title : data.title;
    /** 当前展示用 HTML 正文 */
    const htmlValue = isVersioned ? data.content.content : data.content;

    /**
     * 编辑态确认按钮文案
     */
    const confirmText =
        isVersioned && data.has_signed_record
            ? i18n.t('terms_setting_page_text_save_and_publish')
            : i18n.t('global_btn_save');

    const handleSaveClick = () => {
        if (!titleValue?.trim()) {
            GlobalNotification.error(
                `${i18n.t('return_dish_reasons_page_title')}: ${i18n.t('input_rule_text_required')}`,
            );
            return;
        }
        if (isVersioned) {
            onSave({
                ...data,
                title: data.content.title,
                content: {
                    ...data.content,
                    title: data.content.title,
                    content: data.content.content,
                },
            });
            return;
        }
        onSave(data);
    };

    /**
     * 更新标题
     * @param val - 输入值
     */
    const handleTitleChange = (val: string) => {
        if (isVersioned) {
            setData({
                ...data,
                content: {
                    ...data.content,
                    title: val,
                },
            });
            return;
        }
        setData({ ...data, title: val });
    };

    /**
     * 更新正文 HTML
     * @param html - 富文本 HTML
     */
    const handleContentChange = (html: string) => {
        if (isVersioned) {
            setData({
                ...data,
                content: {
                    ...data.content,
                    content: html,
                },
            });
            return;
        }
        setData({ ...data, content: html });
    };

    return (
        <DialogComponent
            maxWidth="lg"
            open={open}
            heightSize="large"
            sx={{
                '& .MuiDialog-paper': {
                    display: 'flex',
                    flexDirection: 'column',
                },
                '& .MuiDialogContent-root': {
                    flex: '1 1 auto',
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                },
            }}
            dialogContentStyle={{
                paddingTop: '8px',
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}
            defaultHeaderProps={{
                title: isPreview ? i18n.t('general_text_preview') : i18n.t('google_text_detail'),
                onClose,
            }}
            footer={
                isPreview ? (
                    <Button color="primary" variant="contained" onClick={onClose}>
                        {i18n.t('extra_cost_text_close')}
                    </Button>
                ) : (
                    <>
                        <Button color="primary" variant="outlined" onClick={onClose}>
                            {i18n.t('google_text_public_button_cancel')}
                        </Button>
                        <Button color="primary" variant="contained" onClick={handleSaveClick} sx={{ ml: 2 }}>
                            {confirmText}
                        </Button>
                    </>
                )
            }
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    width: '100%',
                }}
            >
                <Box sx={{ mb: 2, width: '100%', maxWidth: 440, flexShrink: 0 }}>
                    <FormFieldCell labelKey="return_dish_reasons_page_title">
                        <CommonInput
                            regularType="required"
                            variant="outlined"
                            fullWidth
                            required
                            hiddenLabel
                            disabled={isTitleDisabled}
                            value={titleValue}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                handleTitleChange(event.target.value);
                            }}
                        />
                    </FormFieldCell>
                </Box>
                {isPreview ? (
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            border: '1px solid var(--stroke-neutral-800-a25)',
                            borderRadius: '8px',
                            p: 2,
                            backgroundColor: 'var(--fill-neutral-100)',
                            color: 'var(--text-dark)',
                            overflow: 'auto',
                        }}
                    >
                        {htmlValue?.trim() ? (
                            <Box className="ql-snow park-mode-terms-detail__quill-preview-root">
                                <Box
                                    component="div"
                                    className="ql-editor park-mode-terms-detail__html-body"
                                    sx={{
                                        '& img': { maxWidth: '100%', height: 'auto' },
                                    }}
                                    dangerouslySetInnerHTML={{ __html: htmlValue }}
                                />
                            </Box>
                        ) : (
                            <Typography variant="body2" sx={{ color: 'var(--text-neutral-500)' }}>
                                {i18n.t('general_text_no_or_more_data')}
                            </Typography>
                        )}
                    </Box>
                ) : (
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <RichTextEditor
                            fillHeight
                            value={htmlValue}
                            onChange={handleContentChange}
                            toolbarId={`park-terms-toolbar-${data.id}`}
                        />
                    </Box>
                )}
            </Box>
        </DialogComponent>
    );
};

export default ParkModeTermsDetailDialog;
